import React, { useContext, useEffect, useMemo, useState } from 'react'
import { themeContext } from 'contexts/theme_context/ThemeContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LicensePlate from 'components/license_plate/LicensePlate';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import { languageContext } from 'contexts/language_context/LanguageContext';
import { VehicleContext } from 'contexts/vehicles_context/VehiclesContext';
import {
  createVehicleComplaint,
  getVehicleComplaintSchema,
} from 'services/vehicleComplaintService';
import './ComplaintForm.css';

const DIAGNOSTIC_FIELDS = [
  { key: 'is_vehicle_able_to_move', copyKey: 'move' },
  { key: 'does_screen_illuminate', copyKey: 'switch' },
  { key: 'does_screen_reflect_changes', copyKey: 'shifter' },
  { key: 'is_battery_ticking_sound', copyKey: 'tick' },
];

const DEFAULT_CATEGORIES = [
  { value: 'complaint', label: 'Complaint' },
  { value: 'customer_service_request', label: 'Customer Service Request' },
  { value: 'suggestion', label: 'Suggestion' },
];

const buildEmptyDiagnostics = () => DIAGNOSTIC_FIELDS.reduce((fields, field) => ({
  ...fields,
  [field.key]: null,
}), {});

const buildIdempotencyKey = () => (
  `vehicle-complaint-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const DEFAULT_SUBMIT_ERROR_MESSAGES = {
  validation: {
    title: 'Please review the highlighted fields',
    message: 'Some required complaint details are missing or need correction.',
  },
  network: {
    title: 'Complaint not submitted',
    message: "We couldn't submit the complaint. Please check the connection and try again.",
  },
  auth: {
    title: 'Session expired',
    message: 'Your session has expired. Please sign in again, then submit the complaint.',
  },
  notFound: {
    title: 'Vehicle unavailable',
    message: "We couldn't find this vehicle for your account.",
  },
  server: {
    title: 'Complaint not submitted',
    message: 'The complaint was not submitted. Please try again.',
  },
};

const getSubmitErrorMessages = (copy = {}) => {
  const submitErrors = copy.submitErrors || {};
  return {
    validation: {
      title: submitErrors.validationTitle || DEFAULT_SUBMIT_ERROR_MESSAGES.validation.title,
      message: submitErrors.validationMessage || DEFAULT_SUBMIT_ERROR_MESSAGES.validation.message,
    },
    network: {
      title: submitErrors.networkTitle || DEFAULT_SUBMIT_ERROR_MESSAGES.network.title,
      message: submitErrors.networkMessage || DEFAULT_SUBMIT_ERROR_MESSAGES.network.message,
    },
    auth: {
      title: submitErrors.authTitle || DEFAULT_SUBMIT_ERROR_MESSAGES.auth.title,
      message: submitErrors.authMessage || DEFAULT_SUBMIT_ERROR_MESSAGES.auth.message,
    },
    notFound: {
      title: submitErrors.notFoundTitle || DEFAULT_SUBMIT_ERROR_MESSAGES.notFound.title,
      message: submitErrors.notFoundMessage || DEFAULT_SUBMIT_ERROR_MESSAGES.notFound.message,
    },
    server: {
      title: submitErrors.serverTitle || DEFAULT_SUBMIT_ERROR_MESSAGES.server.title,
      message: submitErrors.serverMessage || DEFAULT_SUBMIT_ERROR_MESSAGES.server.message,
    },
  };
};

const getSubmitErrorNotice = (err, copy) => {
  const submitMessages = getSubmitErrorMessages(copy);
  if (err?.status === 400) {
    return { ...submitMessages.validation, canRetry: false };
  }

  if (err?.status === 401 || err?.status === 403) {
    return { ...submitMessages.auth, canRetry: false };
  }

  if (err?.status === 404) {
    return { ...submitMessages.notFound, canRetry: false };
  }

  if (err?.isNetworkError || err?.code === 'ECONNABORTED') {
    return { ...submitMessages.network, canRetry: true };
  }

  return { ...submitMessages.server, canRetry: true };
};

const formatDisplayDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const normalizePlateNumber = (plate) => {
  if (Array.isArray(plate)) {
    return plate.flat().filter(Boolean).join(' ');
  }
  return plate || null;
};

const firstErrorMessage = (value, fallback) => {
  if (!value) return fallback;
  if (Array.isArray(value)) return value.join(' ');
  if (typeof value === 'object') {
    return Object.values(value).flat().join(' ') || fallback;
  }
  return String(value);
};

const rootValidationMessage = (value) => (
  firstErrorMessage(value?.non_field_errors || value?.detail || value?.error, '')
);

const firstInvalidFieldId = (errors) => {
  if (!errors || Object.keys(errors).length === 0) return null;
  if (errors.category) return 'complaint-category-complaint';

  const invalidDiagnostic = DIAGNOSTIC_FIELDS.find((field) => (
    errors[field.key] || errors.diagnostics?.[field.key]
  ));
  if (invalidDiagnostic) return `${invalidDiagnostic.key}-yes`;

  if (errors.description) return 'customer_complaint_description';
  return null;
};

const focusFirstInvalidField = (errors) => {
  const fieldId = firstInvalidFieldId(errors);
  if (!fieldId || typeof document === 'undefined') return;

  window.setTimeout(() => {
    document.getElementById(fieldId)?.focus();
  }, 0);
};

const getLocalizedCategoryLabel = (category, copy = {}) => (
  copy.categoryLabels?.[category.value]
  || category.label
  || category.value
);

export default function ComplaintForm() {
  const { webtheme } = useContext(themeContext)
  const isDarkMode = webtheme === 'dark';
  const { weblanguage, jsonfile } = useContext(languageContext)
  const { assignComplaintToVehicle } = useContext(VehicleContext)
  const { readCookie } = useContext(AuthContext)
  const location = useLocation();
  const { id, mileage } = useParams()
  const navto = useNavigate()
  const data = jsonfile?.[weblanguage]?.complainform || {}
  const dropdownTranslations = jsonfile?.[weblanguage]?.dropdowns || {}
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES)
  const [category, setCategory] = useState('complaint')
  const [diagnostics, setDiagnostics] = useState(buildEmptyDiagnostics)
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [schemaLoading, setSchemaLoading] = useState(true)
  const [formNotice, setFormNotice] = useState(null)
  const [idempotencyKey, setIdempotencyKey] = useState(null)
  const [date] = useState(() => formatDisplayDate())
  const plateNumber = useMemo(() => normalizePlateNumber(location?.state), [location?.state])

  useEffect(() => {
    let isMounted = true;
    const token = readCookie();

    const fetchSchema = async () => {
      setSchemaLoading(true);
      try {
        const schema = await getVehicleComplaintSchema({ token });
        if (!isMounted) return;
        const nextCategories = Array.isArray(schema.categories) && schema.categories.length > 0
          ? schema.categories
          : DEFAULT_CATEGORIES;
        setCategories(nextCategories);
        setCategory((currentCategory) => {
          if (nextCategories.some((item) => item.value === currentCategory)) {
            return currentCategory;
          }
          return nextCategories.find((item) => item.value === 'complaint')?.value
            || nextCategories[0]?.value
            || 'complaint';
        });
        setFormNotice(null);
      } catch (err) {
        if (isMounted) {
          setCategories(DEFAULT_CATEGORIES);
          setCategory('complaint');
          setFormNotice({
            title: data.schemaWarningTitle || 'Using default ticket options',
            message: data.schemaWarningMessage || 'The latest complaint form options could not be loaded. You can still submit a vehicle complaint.',
            canRetry: false,
            tone: 'warning',
          });
        }
      } finally {
        if (isMounted) setSchemaLoading(false);
      }
    };

    fetchSchema();
    return () => {
      isMounted = false;
    };
  }, [data.schemaWarningMessage, data.schemaWarningTitle, readCookie])

  const setDiagnosticValue = (fieldName, value) => {
    setDiagnostics((currentDiagnostics) => ({
      ...currentDiagnostics,
      [fieldName]: value,
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: undefined,
      diagnostics: undefined,
    }));
  };

  const validateClientSide = () => {
    const nextErrors = {};
    if (!category) nextErrors.category = data.required || 'This field is required.';
    DIAGNOSTIC_FIELDS.forEach((field) => {
      if (diagnostics[field.key] === null) {
        nextErrors[field.key] = data.required || 'This field is required.';
      }
    });
    if (!description.trim()) {
      nextErrors.description = data.required || 'This field is required.';
    }
    return nextErrors;
  };

  const buildPayload = () => {
    const numericMileage = Number(mileage);
    return {
      category,
      description: description.trim(),
      mileage: Number.isFinite(numericMileage) ? numericMileage : 0,
      diagnostics,
      submitted_at: new Date().toISOString(),
    };
  };

  const postData = async () => {
    const clientErrors = validateClientSide();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormNotice({ ...getSubmitErrorMessages(data).validation, canRetry: false });
      focusFirstInvalidField(clientErrors);
      return;
    }

    const token = readCookie();
    const activeIdempotencyKey = idempotencyKey || buildIdempotencyKey();
    if (!idempotencyKey) setIdempotencyKey(activeIdempotencyKey);

    try {
      setErrors({})
      setFormNotice(null)
      setLoading(true)
      await createVehicleComplaint({
        chassisNumber: id,
        token,
        payload: buildPayload(),
        idempotencyKey: activeIdempotencyKey,
      });
      await assignComplaintToVehicle(token).catch(() => [])
      setIdempotencyKey(null)
      navto(`/single/vehicle/${id}`, { state: { notice: 'complaint-submitted' } })
    } catch (err) {
      const nextNotice = getSubmitErrorNotice(err, data);
      if (err.status === 400 && err.data) {
        setErrors(err.data)
        const rootMessage = rootValidationMessage(err.data);
        setFormNotice(rootMessage
          ? { ...nextNotice, message: rootMessage }
          : nextNotice)
        focusFirstInvalidField(err.data);
      } else {
        setFormNotice(nextNotice)
      }
    } finally {
      setLoading(false)
    }
  };

  const fieldError = (fieldName) => (
    errors[fieldName]
      ? firstErrorMessage(errors[fieldName], data.required || 'This field is required.')
      : ''
  );

  const diagnosticError = (fieldName) => (
    fieldError(fieldName)
    || (errors.diagnostics?.[fieldName]
      ? firstErrorMessage(errors.diagnostics[fieldName], data.required || 'This field is required.')
      : '')
  );

  return (
    <main
      className={`complaint-form-page ${isDarkMode ? 'complaint-form-page--dark' : ''}`}
      dir={weblanguage === 'ar' ? 'rtl' : 'ltr'}
    >
      <section className="complaint-ticket" aria-labelledby="vehicle-complaint-title">
        <header className="complaint-ticket-header">
          <div className="complaint-ticket-identity">
            <LicensePlate plateNumber={plateNumber} />
            <div>
              <h1 id="vehicle-complaint-title" className="complaint-ticket-title">
                {data.title || 'Vehicle complaint'}
              </h1>
              <dl className="complaint-ticket-meta">
                <div>
                  <dt>{data.chassisnum || 'Chassis number'}</dt>
                  <dd>{id}</dd>
                </div>
                <div>
                  <dt>{data.date || 'Current Date'}</dt>
                  <dd>{date}</dd>
                </div>
              </dl>
            </div>
          </div>
        </header>

        {formNotice && (
          <div
            className={`complaint-form-alert complaint-form-alert--${formNotice.tone || 'error'}`}
            role={formNotice.tone === 'warning' ? 'status' : 'alert'}
            aria-live={formNotice.tone === 'warning' ? 'polite' : 'assertive'}
          >
            <div className="complaint-form-alert-copy">
              <strong>{formNotice.title}</strong>
              <p>{formNotice.message}</p>
            </div>
            {formNotice.canRetry && (
              <button
                type="button"
                className="complaint-form-alert-action"
                onClick={postData}
                disabled={loading}
              >
                {data.retry || 'Retry'}
              </button>
            )}
          </div>
        )}

        <form
          className="complaint-ticket-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            postData();
          }}
        >
          <fieldset className="complaint-ticket-section">
            <legend className="complaint-section-title">
              {dropdownTranslations.customerTicketCategory || 'Ticket Category'}
            </legend>
            <div className="complaint-category-options" role="radiogroup" aria-label={dropdownTranslations.customerTicketCategory || 'Ticket Category'}>
              {categories.map(({ value, label }) => (
                <button
                  type="button"
                  key={value}
                  id={`complaint-category-${value}`}
                  onClick={() => {
                    setCategory(value);
                    setErrors((currentErrors) => ({ ...currentErrors, category: undefined }));
                    setFormNotice(null);
                  }}
                  className={`complaint-category-button ${category === value ? 'is-active' : ''}`}
                  aria-pressed={category === value}
                  disabled={schemaLoading || loading}
                >
                  {getLocalizedCategoryLabel({ value, label }, data)}
                </button>
              ))}
            </div>
            {fieldError('category') && (
              <span className="complaint-field-error" role="alert">
                {fieldError('category')}
              </span>
            )}
          </fieldset>

          <fieldset className="complaint-ticket-section">
            <legend className="complaint-section-title">{data.diagnostics || 'Diagnostics'}</legend>
            <div className="complaint-diagnostics-grid">
              {DIAGNOSTIC_FIELDS.map((field) => {
                const hasYesValue = diagnostics[field.key] === true;
                const hasNoValue = diagnostics[field.key] === false;
                const errorMessage = diagnosticError(field.key);

                return (
                  <fieldset
                    key={field.key}
                    className={`complaint-diagnostic-card ${errorMessage ? 'has-error' : ''}`}
                  >
                    <legend className="complaint-diagnostic-question">
                      {data[field.copyKey] || field.key.replace(/_/g, ' ')}
                    </legend>
                    <div className="complaint-radio-row">
                      <label className={`complaint-radio-option ${hasYesValue ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          id={`${field.key}-yes`}
                          name={field.key}
                          className="complaint-radio-input"
                          checked={hasYesValue}
                          onChange={() => setDiagnosticValue(field.key, true)}
                          disabled={loading}
                        />
                        <span>{data.yes || 'Yes'}</span>
                      </label>
                      <label className={`complaint-radio-option ${hasNoValue ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          id={`${field.key}-no`}
                          name={field.key}
                          className="complaint-radio-input"
                          checked={hasNoValue}
                          onChange={() => setDiagnosticValue(field.key, false)}
                          disabled={loading}
                        />
                        <span>{data.no || 'No'}</span>
                      </label>
                    </div>
                    {errorMessage && (
                      <span className="complaint-field-error" role="alert">
                        {errorMessage}
                      </span>
                    )}
                  </fieldset>
                );
              })}
            </div>
          </fieldset>

          <div className="complaint-ticket-section">
            <label htmlFor="customer_complaint_description" className="complaint-section-title">
              {data.complaint || 'Request'}
            </label>
            <textarea
              name="customer_complaint_description"
              id="customer_complaint_description"
              rows="7"
              className={`complaint-description-input ${fieldError('description') ? 'has-error' : ''}`}
              value={description}
              disabled={loading}
              onChange={(event) => {
                setDescription(event.target.value);
                setErrors((currentErrors) => ({ ...currentErrors, description: undefined }));
                setFormNotice(null);
              }}
            />
            {fieldError('description') && (
              <span className="complaint-field-error" role="alert">
                {fieldError('description')}
              </span>
            )}
          </div>

          <footer className="complaint-ticket-footer">
            <button
              id="submit"
              type="submit"
              className="complaint-submit-button"
              disabled={loading}
            >
              {loading ? (
                <span className="complaint-submit-spinner" aria-hidden="true" />
              ) : null}
              <span>{loading ? data.submitting || 'Submitting' : data.submit || 'Submit'}</span>
            </button>
          </footer>
        </form>
      </section>
    </main>
  )
}
